import { ORG_UNIT_TYPES, can } from './identity.constant';
import type { AccessRole, OrgUnitType } from './identity.constant';
import { DEFAULT_LOCALE } from './locale.constant';
import type { Locale } from './locale.constant';
import { DASHBOARD_SHORTCUT_CAPABILITY, isDashboardShortcutTarget } from './dashboardShortcut.constant';
import type { DashboardShortcutTarget } from './dashboardShortcut.constant';
import {
  DASHBOARD_TILES,
  REPEATABLE_TILE_ID,
  isShortcutTileId,
  DASHBOARD_TILE_IDS,
  isDashboardTileId,
  isTileSize,
  tileEntry,
  tileIsAvailable,
} from './dashboardTile.constant';
import type { DashboardTileEntry, DashboardTileId, TileSize } from './dashboardTile.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHICH TILES A DASHBOARD HOLDS, AND HOW THAT IS DECIDED. [P6a]
 *
 *  Layouts are keyed on `(accessRole, orgUnitType)` because a CP-office nodal admin and a
 *  divisional nodal admin are the SAME ROLE and want different tiles —
 *  `identity.constant.ts` says it outright: the CP-office-versus-division distinction is
 *  SCOPE, which node you sit on, not role.
 *
 *  ⚠️ THE RESOLVER IS PURE AND TAKES ITS ROWS AS A PARAMETER. That is what makes the
 *  fallback chain unit-testable over all twelve combinations with no database, and it is
 *  what will let P6b's configurator preview an unsaved edit with the same function the
 *  server answers with. A resolver that read the collection itself would need a second,
 *  drifting implementation for the preview.
 *
 *  ⚠️ AND IT NEVER RETURNS AN EMPTY DASHBOARD TO ANYBODY WHO MAY SEE ONE. An empty page is
 *  the failure this project has spent three UX passes eliminating, so the floor below is a
 *  THEOREM rather than a hope: every `MINIMUM_TILE_IDS` member is meaningful on all three
 *  org types and readable by all four dashboard roles, which `dashboardLayout.constant.test.ts`
 *  asserts as a PROPERTY — so changing the floor's membership is safe and weakening it is red.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [P6b.2] A PLACEMENT, NOT A TILE ID — AND THE DIFFERENCE IS WHAT LETS A SHORTCUT BE USED
 * MORE THAN ONCE.
 *
 * [P6b.1] keyed a layout on tile IDS and de-duplicated them, so "four shortcuts" had to mean
 * four fixed catalogue ids and a ceiling nobody could raise without a code change. A layout is
 * now a list of PLACEMENTS: each carries its own identity, so one repeatable tile can be
 * placed as many times as an operator wants, each with its own destination and its own words.
 *
 * ⚠️ THE ENTRY HOLDS ONLY SCALARS, DELIBERATELY. Its per-locale words and its destination stay
 * in the top-level maps below, keyed on `instanceId` — because a `Map` inside an array
 * sub-document is a shape this repository has measured and does not need, while a sub-document
 * of plain scalars is the simplest thing `strict: 'throw'` does not reach.
 */
export type DashboardLayoutEntry = {
  /**
   * ⚠️ MINTED FROM RANDOMNESS, AND STABLE — §7's identity-not-position rule, fifth instance.
   * A default placement carries a DERIVED id (`d:<TILE_ID>` / `d:<TILE_ID>:<n>`) so the
   * overlay and the dismissal list can name it without the seeder having run.
   */
  readonly instanceId: string;
  /** `string`, never the union: a stored row may name a tile the catalogue has since retired. */
  readonly tileId: string;
  /**
   * ⚠️ WHICH ROW THIS SITS ON, 1-BASED — AND IT IS A GROUP, NOT A COORDINATE.
   *
   * Without it the grid is one continuous flow and a tile lands wherever the previous one left
   * off, so two small tiles an operator wanted side by side get a third pushed up beside them
   * the moment the widths allow. A row ENDS where the operator says it ends, whether or not it
   * is full. Order within a row is array order, which is the operator's own — step 3's rule.
   */
  readonly row: number;
  /** `null` means the catalogue's own size. */
  readonly size: string | null;
};

export type DashboardLayoutRow = {
  readonly accessRole: AccessRole;
  readonly orgUnitType: OrgUnitType;
  /**
   * ⚠️ ORDERED, AND THE ORDER IS THE OPERATOR'S. Step 3 APPENDS for that reason.
   */
  readonly entries: readonly DashboardLayoutEntry[];
  /**
   * ⚠️ KEYED ON A DEFAULT PLACEMENT'S INSTANCE ID, WHICH NARROWS [P6b]'s RULE RATHER THAN
   * REPLACING IT. Only a DEFAULT placement is ever auto-appended, and each one has a derived,
   * stable id — so "I do not want this" can now be said about one of two default shortcuts
   * without saying it about the other, which a tile-id key could not express.
   */
  readonly dismissedInstanceIds: readonly string[];
  /** ⚠️ [C1e §3] Keyed `layoutOverrideKey(instanceId, locale)`, like `titles` beside it. */
  readonly hints?: Readonly<Record<string, string>>;
  /** Keyed by `layoutOverrideKey(instanceId, locale)`. */
  readonly titles?: Readonly<Record<string, string>>;
  /** Keyed by `layoutOverrideKey(instanceId, locale)`. */
  readonly drillLabels?: Readonly<Record<string, string>>;
  /** Keyed by `instanceId`. Only a repeatable navigation tile has one. */
  readonly shortcuts?: Readonly<Record<string, string>>;
};

export const layoutOverrideKey = (instanceId: string, locale: Locale): string => `${instanceId}#${locale}`;

/** A default placement's derived, stable identity. See `dismissedInstanceIds`. */
export const defaultInstanceId = (tileId: DashboardTileId, ordinal = 1): string =>
  ordinal === 1 ? `d:${tileId}` : `d:${tileId}:${String(ordinal)}`;

/**
 * Which rung of the chain answered. On the wire deliberately: a dashboard reporting
 * `DEFAULTS` means the seeder has never run, and that is an operational fact somebody
 * should be able to see without a database console.
 */
export const LAYOUT_RESOLUTION_SOURCES = ['EXACT', 'ROLE', 'DEFAULTS', 'MINIMUM', 'DENIED'] as const;
export type LayoutResolutionSource = (typeof LAYOUT_RESOLUTION_SOURCES)[number];

/**
 * What a row says about ONE tile, resolved. [P6b]
 *
 * ⚠️ `null`, NEVER `''`. A blank override MEANS "use the registry's own word" — it does not
 * mean a tile with no name. An empty `<h3>` on twelve dashboards is a defect nobody can
 * report except as "the tile has no title", and the decision that blank collapses to null is
 * taken HERE and nowhere else, so the write path and the read path cannot disagree about it.
 */
/** What a placement resolves to, once the row and the catalogue have both been read. */
export type ResolvedTileSettings = {
  readonly instanceId: string;
  readonly row: number;
  readonly size: TileSize;
  readonly titleOverride: string | null;
  readonly drillLabelOverride: string | null;
  /**
   * ⚠️ [C1e §3] A TRI-STATE, AND THE ONLY ONE ON THIS TYPE.
   *
   *   null → the registry's own sentence      '' → the line is ABSENT      'x' → 'x'
   */
  readonly hintOverride: string | null;
  /** ⚠️ `null` on every tile that is not a shortcut, and on a shortcut with no destination. */
  readonly shortcutTarget: DashboardShortcutTarget | null;
};

export type ResolvedDashboardTile = DashboardTileEntry & ResolvedTileSettings;

/**
 * ⚠️ THE ENTRY GREW AND THE ENVELOPE DID NOT, WHICH IS WHAT KEEPS THE EXISTING SUITE HONEST.
 * `dashboardLayout.constant.test.ts` asserts `toEqual({ source: 'DENIED', tiles: [] })` on the
 * fail-closed rung — an exact match, so a new top-level key here would turn the one test that
 * proves this thing fails closed red, and the temptation would be to loosen the assertion.
 */
export type ResolvedDashboardLayout = {
  readonly source: LayoutResolutionSource;
  readonly tiles: readonly ResolvedDashboardTile[];
};

/**
 * ⚠️ THE FLOOR. Its membership is constrained rather than chosen: every member has `scopes`
 * covering all three org types and a capability every dashboard role holds, so steps 5 and 6
 * CANNOT empty it for a caller who got past step 0.
 *
 * ⚠️ `REQUIREMENT_FULFILMENT` IS DELIBERATELY NOT HERE even though it is operational and
 * defaults to everybody: it is COMMISSIONERATE-only, so it would be dropped at a division
 * and the floor would stop being a floor exactly where it is needed.
 */
export const MINIMUM_TILE_IDS = [
  'ORDERS_BY_STATUS',
  'LINES_UNNAMED',
  'DUTIES_UNSTAFFED',
] as const satisfies readonly DashboardTileId[];

/**
 * ⚠️ THE ARRAY IS WIDENED FOR THE `includes` TEST rather than the role being narrowed —
 * `POLICE_STATION_SUBTYPES`' idiom. `NONE` matches nothing here, which is correct and needs
 * no branch: it is refused at step 0 anyway, and this stays total.
 */
export const defaultTileIdsFor = (accessRole: AccessRole): readonly DashboardTileId[] =>
  DASHBOARD_TILE_IDS.filter((id) => (DASHBOARD_TILES[id].defaultFor as readonly AccessRole[]).includes(accessRole));

/**
 * ⚠️ THE SEEDED DEFAULT, AND THERE IS EXACTLY ONE EXPRESSION OF IT. [P6b]
 *
 * `dashboardLayout.plan.ts` is a projection of this, because P6b's "reset this combination to
 * its default" has to land on precisely what the seeder would have written. Two copies of a
 * projection is `orgUnitDocFields`' lesson — the four transcriptions of `ancestors` that had
 * already drifted — one layer up.
 *
 * ⚠️ [amended P6b.2] IT RETURNS PLACEMENTS NOW, NOT IDS, AND THAT IS WHAT MAKES "TWO
 * SHORTCUTS BY DEFAULT" EXPRESSIBLE AT ALL. `defaultFor` is per TILE and cannot say "twice";
 * the default LAYOUT can. The two doors [P6a] deleted with `pages/Home` sit on row 1 — small,
 * side by side, and above the numbers, which is where that page had them — and the operational
 * figures follow on rows 2 and 3.
 */
export const defaultLayoutEntriesFor = (
  accessRole: AccessRole,
  orgUnitType: OrgUnitType,
): readonly DashboardLayoutEntry[] => {
  const shortcuts = DEFAULT_SHORTCUT_PLACEMENTS.map((target, index) => ({
    instanceId: defaultInstanceId(REPEATABLE_TILE_ID, index + 1),
    tileId: REPEATABLE_TILE_ID,
    row: 1,
    size: null,
    target,
  })).filter((entry) => can(accessRole, DASHBOARD_SHORTCUT_CAPABILITY[entry.target]));

  /*
   * ⚠️ [amended C1e §1a] THE ROW ASSIGNMENT IS SIZE-AWARE NOW, and it had to become so.
   *
   * It was a flat `index < 3 ? 2 : 3` — three to a row, which is right for the six `sm`/`md`
   * figures it was written for. `ARRANGEMENTS_TODAY` is `lg`, and `TILE_SPAN.lg` spans the
   * WHOLE column count at every rung, so sharing a row with anything makes the other tile wrap
   * beneath it inside that row's own grid. A full-width tile therefore opens a row and closes
   * it, which is exactly what a `lg` size is a statement about.
   *
   * ⚠️ THIS IS THE ONE EXPRESSION OF THE DEFAULT LAYOUT. `dashboardLayout.plan.ts` is a
   * PROJECTION of this function, so the seeder writes what the resolver would have answered —
   * `orgUnitDocFields`' lesson, one layer up, and the reason a second copy is not tolerated.
   */
  const eligible = defaultTileIdsFor(accessRole).filter((id) => tileIsAvailable(id, orgUnitType, accessRole));

  let row = 1;
  let inRow = 3;
  const numbered = eligible.map((id) => {
    const full = DASHBOARD_TILES[id].size === 'lg';
    if (full || inRow >= 3) {
      row += 1;
      inRow = 0;
    }
    inRow = full ? 3 : inRow + 1;
    return { instanceId: defaultInstanceId(id), tileId: id, row, size: null };
  });

  return [...shortcuts.map(({ target: _target, ...entry }) => entry), ...numbered];
};

/** The destination each default shortcut placement points at, in order. */
export const DEFAULT_SHORTCUT_PLACEMENTS = [
  'INBOX',
  'ARRANGEMENTS',
] as const satisfies readonly DashboardShortcutTarget[];

/**
 * ⚠️ WHERE A DEFAULT DOOR POINTS BEFORE ANYBODY HAS SAVED ANYTHING.
 *
 * A shortcut's destination is stored against its placement, and a default placement has no
 * stored row to be stored in — so without this the two doors overlaid by step 3 resolve to
 * `null`, are dropped by step 6 as pointing nowhere, and "restore what `pages/Home` showed"
 * restores nothing at all on the database that needs it most. Derived from the list above, so
 * the ordinal and the destination cannot part company.
 */
const DEFAULT_SHORTCUT_BY_INSTANCE: Readonly<Record<string, DashboardShortcutTarget>> = Object.fromEntries(
  DEFAULT_SHORTCUT_PLACEMENTS.map((target, index) => [defaultInstanceId(REPEATABLE_TILE_ID, index + 1), target]),
);

/**
 * ⚠️ WHAT A STORED ROW SAYS ABOUT ONE PLACEMENT — THE ONE ACCESSOR, AND IT IS WHAT STOPS
 * P6b'S CONFIGURATOR BEING A SECOND RESOLVER.
 *
 * ⚠️ A STORED SIZE THE CATALOGUE NO LONGER KNOWS FALLS BACK rather than escaping — step 4's
 * rule ("the code wins on existence") applied to a VALUE instead of to an id. Without it a
 * retired `TileSize` would reach `TILE_SPAN[size]` on the web and render no span class at all.
 */
export const layoutTileSettings = (
  row: DashboardLayoutRow | undefined,
  entry: DashboardLayoutEntry,
  locale: Locale = DEFAULT_LOCALE,
): ResolvedTileSettings => {
  const key = layoutOverrideKey(entry.instanceId, locale);
  /* ⚠️ `??` WOULD PASS `''` THROUGH, and `''` is exactly what must not reach a heading. */
  const title = row?.titles?.[key]?.trim();
  const drill = row?.drillLabels?.[key]?.trim();
  /* ⚠️ [C1e §3] AND HERE `''` IS EXACTLY WHAT MUST SURVIVE — see the reduction below. */
  const hint = row?.hints?.[key]?.trim();
  const storedTarget = row?.shortcuts?.[entry.instanceId];
  const catalogueSize = isDashboardTileId(entry.tileId) ? DASHBOARD_TILES[entry.tileId].size : 'sm';

  return {
    instanceId: entry.instanceId,
    /* ⚠️ A ROW BELOW 1 IS COERCED RATHER THAN REFUSED — the resolver never rejects a stored
       value, it falls back. The write path is what refuses one. */
    row: Number.isFinite(entry.row) && entry.row >= 1 ? Math.floor(entry.row) : 1,
    size: entry.size !== null && isTileSize(entry.size) ? entry.size : catalogueSize,
    titleOverride: title !== undefined && title.length > 0 ? title : null,
    drillLabelOverride: drill !== undefined && drill.length > 0 ? drill : null,
    /*
     * ⚠️ [C1e §3] A DIFFERENT REDUCTION FROM THE TWO ABOVE, AND THE CONTRAST IS THE POINT.
     *
     * Up there `''` collapses to `null` and `null` means "use the registry's word". Here `''`
     * is a THIRD STATE meaning "do not render the line at all", so only a MISSING KEY may
     * become `null`. `?.trim()` yields `undefined` for an absent key and `''` for a blank
     * one; `?? null` maps the first and PRESERVES the second. Writing this the same way as
     * the labels would erase the very state the tri-state exists for.
     */
    hintOverride: hint ?? null,
    shortcutTarget:
      isDashboardTileId(entry.tileId) && isShortcutTileId(entry.tileId)
        ? storedTarget !== undefined && isDashboardShortcutTarget(storedTarget)
          ? storedTarget
          : /* ⚠️ The catalogue's own default, so an unconfigured database still shows the doors. */
            (DEFAULT_SHORTCUT_BY_INSTANCE[entry.instanceId] ?? null)
        : null,
  };
};

/** Step 1's borrow: the same role at another org type, in a DETERMINISTIC order. */
const borrowRowForRole = (
  rows: readonly DashboardLayoutRow[],
  accessRole: AccessRole,
): DashboardLayoutRow | undefined => {
  for (const orgUnitType of ORG_UNIT_TYPES) {
    const row = rows.find((candidate) => candidate.accessRole === accessRole && candidate.orgUnitType === orgUnitType);
    if (row) return row;
  }
  return undefined;
};

/**
 * ⚠️ STEP 1, EXTRACTED — SO THE CONFIGURATOR CAN SAY WHICH ROW ANSWERED WITHOUT RE-DERIVING
 * THE BORROW. [P6b]
 */
export const pickDashboardLayoutRow = (input: {
  readonly accessRole: AccessRole;
  readonly orgUnitType: OrgUnitType;
  readonly rows: readonly DashboardLayoutRow[];
}): { readonly row: DashboardLayoutRow | undefined; readonly source: 'EXACT' | 'ROLE' | 'DEFAULTS' } => {
  const exact = input.rows.find((row) => row.accessRole === input.accessRole && row.orgUnitType === input.orgUnitType);
  const row = exact ?? borrowRowForRole(input.rows, input.accessRole);
  return { row, source: exact ? 'EXACT' : row ? 'ROLE' : 'DEFAULTS' };
};

/**
 * THE FALLBACK CHAIN, in seven ordered steps.
 *
 *   0  FAIL CLOSED — no `dashboard.access`, no dashboard. `DENIED` with no tiles.
 *   1  PICK THE ROW: exact `(role, type)`, else the same ROLE at another type, else none.
 *   2  Start from the placements that row names, or from none.
 *   3  OVERLAY the default PLACEMENTS, appending any the row neither holds nor dismissed.
 *   4  DROP ids the catalogue does not know.       ← code wins on EXISTENCE
 *   5  DROP tiles this dashboard cannot mean.      ← code wins on MEANING
 *   6  DROP tiles this role cannot read, and shortcuts it cannot follow.  ← code wins on ACCESS
 *   7  Still empty? The floor, through 4-6. `MINIMUM`.
 *
 * ⚠️ WHO WINS, AND WHEN. The stored row wins on ORDER, on ROW GROUPING, on the presence of
 * what it names and on the absence of what it dismissed. The code wins on existence, meaning
 * and access — steps 4-6 are unconditional and run AFTER the overlay.
 *
 * ⚠️ STEP 3 APPENDS AND NEVER INSERTS, and it appends to the LAST row rather than opening a
 * new one: a tile shipped later should join the arrangement, not push it down.
 *
 * ⚠️ [amended P6b.2] DE-DUPLICATION IS BY INSTANCE, AND BY TILE ID ONLY FOR A TILE THAT
 * CANNOT REPEAT. That is the whole of what makes a shortcut placeable more than once — and
 * the guard that stops `ATTENDANCE` being placed twice, which would render one number twice
 * and make the page lie about how much is on it.
 */
export const resolveDashboardLayout = (input: {
  readonly accessRole: AccessRole;
  readonly orgUnitType: OrgUnitType;
  readonly rows: readonly DashboardLayoutRow[];
  /** ⚠️ OPTIONAL AND DEFAULTED, so every existing caller compiles. */
  readonly locale?: Locale;
}): ResolvedDashboardLayout => {
  const { accessRole, orgUnitType, rows, locale = DEFAULT_LOCALE } = input;

  if (!can(accessRole, 'dashboard.access')) return { source: 'DENIED', tiles: [] };

  const { row, source } = pickDashboardLayoutRow({ accessRole, orgUnitType, rows });

  const seenInstances = new Set<string>();
  const seenSingletons = new Set<string>();
  const ordered: DashboardLayoutEntry[] = [];

  const admit = (entry: DashboardLayoutEntry): void => {
    if (seenInstances.has(entry.instanceId)) return;
    const singleton = !isDashboardTileId(entry.tileId) || !DASHBOARD_TILES[entry.tileId].repeatable;
    if (singleton && seenSingletons.has(entry.tileId)) return;
    seenInstances.add(entry.instanceId);
    if (singleton) seenSingletons.add(entry.tileId);
    ordered.push(entry);
  };

  for (const entry of row?.entries ?? []) admit(entry);

  const dismissed = new Set(row?.dismissedInstanceIds ?? []);
  const placedTiles = new Set(ordered.map((entry) => entry.tileId));
  const lastRow = ordered.reduce((highest, entry) => Math.max(highest, entry.row), 1);
  for (const entry of defaultLayoutEntriesFor(accessRole, orgUnitType)) {
    if (dismissed.has(entry.instanceId)) continue;
    /*
     * ⚠️ [P6b.2] A DEFAULT PLACEMENT LANDS ONLY IF THAT TILE IS NOT PLACED AT ALL.
     *
     * For a singleton this is what the de-duplication below already did. For a REPEATABLE tile
     * it is the rule that had to be written down: an operator who has arranged three doors has
     * said what they want, and appending the two defaults on top would give them five — every
     * time the page loaded, with no way to refuse short of dismissing defaults they never saw.
     */
    if (placedTiles.has(entry.tileId)) continue;
    /* Appended to the arrangement that exists, never above it. */
    admit(ordered.length === 0 ? entry : { ...entry, row: lastRow });
  }

  const usable = ordered
    .filter((entry) => isDashboardTileId(entry.tileId))
    .filter((entry) => tileIsAvailable(entry.tileId as DashboardTileId, orgUnitType, accessRole))
    /*
     * ⚠️ STEP 6, REACHING A VALUE THE DEFINITION DOES NOT HOLD. A shortcut's `capability` is
     * `dashboard.access`, but where it POINTS is per-placement configuration — so a role whose
     * access narrowed after the layout was saved would otherwise keep a card whose only
     * outcome is a bounce off `/forbidden`. A shortcut with no destination goes too: "zero
     * does not link", one tile over.
     */
    .filter((entry) => {
      if (!isShortcutTileId(entry.tileId as DashboardTileId)) return true;
      const target = layoutTileSettings(row, entry, locale).shortcutTarget;
      return target !== null && can(accessRole, DASHBOARD_SHORTCUT_CAPABILITY[target]);
    });

  const decorate = (entry: DashboardLayoutEntry): ResolvedDashboardTile => ({
    ...tileEntry(entry.tileId as DashboardTileId),
    ...layoutTileSettings(row, entry, locale),
  });

  if (usable.length > 0) return { source, tiles: usable.map(decorate) };

  /*
   * ⚠️ THE FLOOR IS DECORATED FROM THE SAME ROW, DELIBERATELY. If a tile the operator renamed
   * appears at all — even as the last resort — it appears under the name they gave it.
   */
  const floor = MINIMUM_TILE_IDS.filter((id) => tileIsAvailable(id, orgUnitType, accessRole)).map((id) => ({
    instanceId: defaultInstanceId(id),
    tileId: id,
    row: 1,
    size: null,
  }));
  return { source: 'MINIMUM', tiles: floor.map(decorate) };
};

/**
 * ⚠️ [P6b.2] THE PLACEMENTS, GROUPED INTO THE ROWS THE OPERATOR DREW.
 *
 * ⚠️ GENERIC OVER ANYTHING WITH A `row`, so the SERVER's resolved placement and the CLIENT's
 * tile envelope go through the same function. A version typed to one of them would have been
 * copied for the other, and "which tiles are on row 2" is exactly the kind of arithmetic that
 * drifts when two screens each do it.
 *
 * One derivation, shared by the dashboard, the configurator's preview and any client that
 * renders a layout — because "which tiles are on row 2" is exactly the kind of arithmetic that
 * drifts when two screens each do it. Rows are RENUMBERED to be contiguous from 1: a stored
 * row of `[1, 1, 4]` is three tiles on two rows, and a gap is not a blank row an operator
 * asked for.
 */
export const groupTilesByRow = <T extends { readonly row: number }>(tiles: readonly T[]): readonly (readonly T[])[] => {
  const rows = [...new Set(tiles.map((tile) => tile.row))].sort((a, b) => a - b);
  return rows.map((row) => tiles.filter((tile) => tile.row === row)).filter((group) => group.length > 0);
};
